import { Module } from 'vuex'
import { PTRootState } from '@pt/store/index.type'
import {
  AnimationData,
  CameraAdaptConfig,
  HsvValue,
  ModelEditorProtocol,
  ModelEditorProtocolDataType,
  ModelEditorProtocolMessage,
  ModelModify,
  ModelModifyData,
  ModelResId,
  ReplaceData,
  ShaderParams,
} from '@/store/creative/model-editor/index.type'
import _ from 'lodash'
import { Message } from 'element-ui'
import { getFileName } from '@com/utils/file'
import {
  CookedCategory,
  ModelAnimation,
  ModelMaterial,
} from '@/types/dmaker/category.type'
import {
  getAnimationList,
  updateAnimationListByConfig,
} from '@/views/creative/utils/model-animation-utils'
import { isFeModelId } from '@/store/creative/common/category'
import mutations from './mutations'
import useIdToFindCategory from '@/views/creative/utils/use-id-to-find-category'
import {
  getAllSkinTexturePrefixByCategory,
  getMaterialList,
} from '@/views/creative/utils/model-material-utils'

export type HsvModel = {
  hub: number
  saturation: number
  value: number
  disabled: boolean
}

export type BasicModel = {
  translateX: number
  translateY: number
  translateZ: number
  rotateX: number
  rotateY: number
  rotateZ: number
  scaleX: number
  scaleY: number
  scaleZ: number
}

export type ModelAnimationMap = {
  thumbUrl: string
  resId: number | string
  resSource: 'common' | 'assets'
}

export interface ModelEditorState {
  category: CookedCategory // 当前编辑的模型分类（解耦 currentCategory 的依赖，避免动作库赋值 currentCategory 的时候逻辑影响）
  loading: boolean // 加载中
  replaceModel: boolean // 是否替换模型
  modelLoading: boolean // 是否加载模型
  visible: boolean // 弹框是否可见
  modelPath: string // 当前编辑的模型路径
  materialPath: string // 当前编辑的材质路径
  animationPath: string // 当前编辑的动画路径
  modelId: ModelResId // 当前编辑的模型id
  materialId: ModelResId // 当前编辑的材质 id
  animationId: AnimationData['id'] // 当前编辑的动画 id
  hsvValue: HsvValue[] | null // 当前编辑的材质 hsv 值
  modelModifyConfig: ModelModify
  hsvModel: HsvModel[] // 色彩模型
  basicModel: BasicModel
  shaderModel: Partial<ShaderParams>
  animationModel: Partial<AnimationData>
  cameraAdaptConfig: CameraAdaptConfig
  replaceData: ReplaceData[]
  modelMaterialList: ModelMaterial[]
  modelAnimationList: ModelAnimation[]
  replaceMap: Record<CookedCategory['id'], ModelAnimationMap>
  resetModel: Record<string, any> // 模型编辑弹框打开时的初始状态，key/value 有可能是 modelId/materialId/animationId, value 的值也一样， 为什么不直接用 category 里面的数据，因为有可能 category 里面会没有数据
}

const ModelEditor: Module<ModelEditorState, PTRootState> = {
  namespaced: true,
  state: {
    category: {},
    loading: false,
    replaceModel: false,
    modelLoading: false,
    visible: false,
    modelPath: '',
    materialPath: '',
    animationPath: '',
    modelId: '',
    materialId: '',
    animationId: '',
    hsvValue: null,
    modelModifyConfig: {},
    shaderModel: {},
    animationModel: {
      id: '',
      speed: 1,
      duration: 0,
      frameRate: 0,
      rotate: 0,
      events: [],
    },
    hsvModel: [
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: false,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: false,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: false,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: false,
      },
    ],
    basicModel: {
      translateX: 0,
      translateY: 0,
      translateZ: 0,
      rotateX: 0,
      rotateY: 0,
      rotateZ: 0,
      scaleX: 1,
      scaleY: 1,
      scaleZ: 1,
    },
    cameraAdaptConfig: {
      adapt: {
        adaptDatas: [],
      },
      orbitParams: [],
    },
    replaceData: [],
    modelMaterialList: [],
    modelAnimationList: [],
    replaceMap: {},
    resetModel: {},
  },

  mutations: mutations,

  actions: {
    addListenerEvent({ state, commit, dispatch }) {
      window.addEventListener(
        'message',
        (event: MessageEvent & { data: ModelEditorProtocolMessage }) => {
          // 如果弹框不可见，不需要执行回调（避免详情弹框执行以下逻辑）
          if (!state.visible) return
          if (
            event.data &&
            event.data.type &&
            event.data.type === ModelEditorProtocol
          ) {
            const modifyData: ModelModifyData = event.data.data.data
            console.log('modelData', modifyData)
            const refreshData = !modifyData.partialRefresh
            switch (event.data.data.type) {
              /**
               * 预览数据准备完成
               * 该事件会在模型编辑器加载完成或者切换动作以及材质时触发
               * 1. 加载模型完成（包含替换模型以及替换动作）：
               *   a. 赋值模型 id 以及材质 id（如有）
               *   b. 根据当前模型 id 以及材质 id 获取当前的模型修改参数，如果没有内置的调整，则使用 iframe 返回的参数
               *   c. 重置参数，根据第二步拿到的值触发 modal 的值调整，并且记录第二步产生的值作为重置属性，编辑面板重置时使用
               *   d. 驱动 iframe 响应模型参数调整，以达到预览目的
               *   e. 如果是替换动画，需要重新触发切换动画
               * 2. 切换动画、材质
               *   a. 赋值新的材质、动画 id
               *   b. 根据当前的材质 id 获取当前的材质修改参数，如果没有内置的调整，则使用 iframe 返回的参数
               *   c. 重置参数，根据第二步拿到的值触发 modal 的值调整，并且记录第二步产生的值作为重置属性，编辑面板重置时使用
               *   d. 驱动 iframe 响应模型参数调整，以达到预览目的
               */
              case ModelEditorProtocolDataType.previewReady:
                // 如果需要重置内容，重置所有参数
                if (refreshData) {
                  commit('setModelId', '')
                  commit('setMaterialId', '')
                  commit('setAnimationId', '')
                  commit('setHsvValue', null)
                  commit('resetBasicModel')
                  commit('resetHsvModel')
                  commit('resetShaderModel')
                  commit('resetAnimationModel')
                } else {
                  // 局部更新，只重置当前更新的数据
                  if (modifyData.material) {
                    commit('setMaterialId', '')
                    commit('setHsvValue', null)
                    commit('resetHsvModel')
                    commit('resetShaderModel')
                  }
                  commit('setAnimationId', '')
                  commit('resetAnimationModel')
                }
                if (modifyData.transform) {
                  commit('setModelId', modifyData.transform.id)
                }

                if (modifyData.material) {
                  commit('setMaterialId', modifyData.material.id)
                }
                if (modifyData.animation) {
                  commit('setAnimationId', modifyData.animation.id)
                }

                // 如果是重置，并且当前有 animationPath 代表是替换动画，需要重新赋值，触发 iframe 渲染
                if (refreshData && state.animationPath) {
                  const oldPath = state.animationPath
                  commit('setAnimationPath', '')
                  setTimeout(() => {
                    commit('setAnimationPath', oldPath)
                  }, 100)
                }
                setTimeout(() => {
                  dispatch('syncModelModifyConfigToStore', {
                    modifyData,
                    refreshData,
                  })
                  commit('updateLoading', false)
                })
                break
              case ModelEditorProtocolDataType.linkageUpdate:
                if (state.animationModel) {
                  const temp = _.cloneDeep(state.animationModel)
                  if (temp.events && temp.events[modifyData.index]) {
                    temp.events[modifyData.index].time = modifyData.time
                  }
                  commit('setAnimationModel', temp)
                }
                break
              default:
                break
            }
          }
        },
      )
    },
    /**
     * 通过 gltf 替换动画
     * @param commit
     * @param dispatch
     * @param fileUrl gltf 文件路径；用于模型替换脚本传参
     * @param filePath 替换的动画文件路径
     * @param fileSource 文件来源
     * @param mod 模块，分 gm 和 dm ；用于模型替换脚本传参
     * @param model 模型路径；用于模型替换脚本传参
     * @param tplId 模板 ID ；用于模型替换脚本传参
     * @param preset 是否是预设坑位，传 1/0 ；用于模型替换脚本传参
     * @param resId 当前动画的资源 id ，用于更新封面图
     * @param thumbUrl 当前动画库的封面，用于替换后展示
     * @param categoryId 当前替换动画分类 id
     * @param prefix 当前替换动画 prefix
     */
    async replaceAnimationByGltf(
      { state, commit, dispatch },
      {
        fileUrl,
        filePath,
        fileSource,
        mod,
        model,
        tplId,
        preset,
        resId,
        thumbUrl,
        categoryId,
        prefix,
      },
    ) {
      try {
        commit('setReplaceModel', true)
        commit('setModelLoading', true)
        const res = await dispatch(
          'creative/template/model/convertCocosModel',
          {
            file_url: fileUrl,
            mod: mod,
            model: model,
            tpl_id: tplId,
            preset: preset,
          },
          { root: true },
        )
        if (res.data.code === 0) {
          console.log(res.data)
          const files =
            _.get(res, 'data.files') || _.get(res, 'data.data.files')
          const data: ModelEditorState['replaceData'] = _.cloneDeep(
            state.replaceData,
          )
          files.forEach((file: string) => {
            data.push({
              type: 'assetsPackage',
              key: getFileName(file),
              value: file,
              prefix,
            })
          })
          const temp = _.cloneDeep(state.replaceMap)
          temp[categoryId] = {
            thumbUrl,
            resId,
            resSource: fileSource,
          }
          // 如果是替换动作，需要清空动作里面的原 events ,使用素材内的数据
          if (
            state.animationId &&
            state.modelModifyConfig &&
            state.modelModifyConfig.animations &&
            state.modelModifyConfig.animations &&
            state.modelModifyConfig.animations.some(
              (item) =>
                JSON.stringify(item.id) === JSON.stringify(state.animationId),
            ) &&
            state.modelModifyConfig.animations.find(
              (item) =>
                JSON.stringify(item.id) === JSON.stringify(state.animationId),
            ).events &&
            state.modelModifyConfig.animations.find(
              (item) =>
                JSON.stringify(item.id) === JSON.stringify(state.animationId),
            ).events.length
          ) {
            state.modelModifyConfig.animations.find(
              (item) =>
                JSON.stringify(item.id) === JSON.stringify(state.animationId),
            ).events = []
          }
          commit('setReplaceMap', temp)
          commit('setModelLoading', false)
          commit('setReplaceData', data)
        } else {
          Message.error(res.data.msg)
        }
      } catch (e) {
        console.error(e)
      }
      console.log(fileUrl, filePath, mod, model, tplId, preset, resId)
    },
    async openModelEditorByGltf(
      { commit, dispatch, state, rootState },
      {
        fileUrl,
        filePath,
        fileSource,
        mod,
        model,
        tplId,
        preset,
        resId,
        thumbUrl,
        categoryId,
      },
    ) {
      // 以 gltf 格式打开弹框，需要先转成 cocos 之后，再打开
      try {
        commit('setReplaceModel', true)
        commit('setModelLoading', true)
        commit('showModelEditor', { modelPath: filePath })
        const targetCategory = useIdToFindCategory({
          category: rootState.creative.template.templateConfig.category,
          categoryId,
        })
        const allSkinTexturePrefix =
          getAllSkinTexturePrefixByCategory(targetCategory)
        const res = await dispatch(
          'creative/template/model/convertCocosModel',
          {
            file_url: fileUrl,
            mod: mod,
            model: model,
            tpl_id: tplId,
            preset: preset,
            textures: allSkinTexturePrefix,
          },
          { root: true },
        )
        if (res.data.code === 0) {
          const files =
            _.get(res, 'data.files') || _.get(res, 'data.data.files')
          const textures =
            _.get(res, 'data.textures') || _.get(res, 'data.data.textures')
          const data: ModelEditorState['replaceData'] = _.cloneDeep(
            state.replaceData,
          )
          files.forEach((file: string) => {
            data.push({
              type: 'assetsPackage',
              key: getFileName(file),
              value: file,
            })
          })
          if (textures && textures.length) {
            textures.forEach(
              (tex: {
                prefix: string
                file_path: string
                url: string
                file: string
              }) => {
                data.push({
                  type: 'assetsPackage',
                  key: getFileName(tex.file_path),
                  prefix: tex.prefix,
                  file: tex.file,
                  value: tex.url,
                })
              },
            )
          }
          const temp = _.cloneDeep(state.replaceMap)
          temp[categoryId] = {
            thumbUrl,
            resId,
            resSource: fileSource,
          }
          commit('setReplaceMap', temp)
          commit('setModelLoading', false)
          commit('setReplaceData', data)
        } else {
          Message.error(res.data.msg)
        }
      } catch (e) {
        console.error(e)
      }
    },

    syncJsonConfigToModelModifyConfig({ state, rootState }) {
      state.modelModifyConfig = _.get(
        rootState,
        'creative.template.templateConfig.json_config.modelModifyConfig',
      )
    },

    // 同步当前的数据到 config 中
    syncStoreToModelModifyConfig({ state }) {
      if (state.modelId) {
        if (!state.modelModifyConfig.transforms) {
          state.modelModifyConfig.transforms = []
        }
        // 删除之前的 config
        state.modelModifyConfig.transforms =
          state.modelModifyConfig.transforms.filter((item) => {
            return !(JSON.stringify(item.id) === JSON.stringify(state.modelId))
          })
        state.modelModifyConfig.transforms.push({
          id: state.modelId,
          value: [
            state.basicModel.translateX,
            state.basicModel.translateY,
            state.basicModel.translateZ,
            state.basicModel.rotateX,
            state.basicModel.rotateY,
            state.basicModel.rotateZ,
            state.basicModel.scaleX,
            state.basicModel.scaleY,
            state.basicModel.scaleZ,
          ],
        })
      }
      if (state.materialId) {
        if (!state.modelModifyConfig.materials) {
          state.modelModifyConfig.materials = []
        }
        // 删除之前的 config
        state.modelModifyConfig.materials =
          state.modelModifyConfig.materials.filter((item) => {
            return !(
              JSON.stringify(item.id) === JSON.stringify(state.materialId)
            )
          })
        const materialParams = {
          id: state.materialId,
        }
        const value: HsvValue[] = []
        if (state.hsvValue) {
          state.hsvModel.forEach((item) => {
            if (item.disabled) {
              value.push(null)
            } else {
              value.push([item.hub, item.saturation, item.value])
            }
          })
          materialParams.hsvs = value
        }
        if (!_.isEmpty(state.shaderModel)) {
          materialParams.shaderParams = state.shaderModel
        }
        state.modelModifyConfig.materials.push(materialParams)
      }
      if (state.animationId) {
        if (!state.modelModifyConfig.animations) {
          state.modelModifyConfig.animations = []
        }
        // 删除之前的 config
        state.modelModifyConfig.animations =
          state.modelModifyConfig.animations.filter((item) => {
            return !(
              JSON.stringify(item.id) === JSON.stringify(state.animationId)
            )
          })
        const getEvents = () => {
          if (
            state.animationModel.events &&
            state.animationModel.events.length
          ) {
            return state.animationModel.events.map((item) => {
              if (!(item && item.type)) return null
              return item.time
            })
          }
          return []
        }
        state.modelModifyConfig.animations.push({
          id: state.animationId,
          speed: state.animationModel.speed,
          events: getEvents(),
        })
      }
    },

    /**
     * 同步 modelModifyConfig 到 store
     * @param state
     * @param commit
     * @param modifyData 如果没有内置的调整数据，则使用 modifyData 的值
     * @param refreshData 是否是全局刷新，如果不是的话，通过 modifyData 的内容判断究竟更新什么
     */
    syncModelModifyConfigToStore(
      { state, commit },
      {
        modifyData,
        refreshData,
      }: {
        modifyData: ModelModifyData
        refreshData: boolean
      },
    ) {
      const updateTransforms = refreshData
      const updateMaterial =
        refreshData || (!refreshData && modifyData.material)
      const updateAnimation =
        refreshData || (!refreshData && modifyData.animation)
      // 因为 id 有可能是 string 和 string[]，所以 json stringify 之后再对比
      if (
        updateTransforms &&
        state.modelId &&
        state.modelModifyConfig.transforms &&
        state.modelModifyConfig.transforms.length &&
        state.modelModifyConfig.transforms.some(
          (item) => JSON.stringify(item.id) === JSON.stringify(state.modelId),
        )
      ) {
        const transformValue = state.modelModifyConfig.transforms.find(
          (item) => JSON.stringify(item.id) === JSON.stringify(state.modelId),
        )!.value
        commit('setBasicModel', {
          translateX: transformValue[0],
          translateY: transformValue[1],
          translateZ: transformValue[2],
          rotateX: transformValue[3],
          rotateY: transformValue[4],
          rotateZ: transformValue[5],
          scaleX: transformValue[6],
          scaleY: transformValue[7],
          scaleZ: transformValue[8],
        })
        if (!state.resetModel[JSON.stringify(state.modelId)]) {
          const resetTemp = {}
          resetTemp[JSON.stringify(state.modelId)] = transformValue
          commit('setResetModel', resetTemp)
        }
      }

      // 当模型含有 hsv 的值，或者 modelModifyConfig 含有对应的值，则复制 hsvModel，后者优先级更高
      if (updateMaterial && state.materialId) {
        let hsvValue = modifyData.material?.hsv
        if (
          state.modelModifyConfig &&
          state.modelModifyConfig.materials &&
          state.modelModifyConfig.materials.some(
            (item) =>
              JSON.stringify(item.id) === JSON.stringify(state.materialId) &&
              item.hsvs,
          )
        ) {
          hsvValue = state.modelModifyConfig.materials.find(
            (item) =>
              JSON.stringify(item.id) === JSON.stringify(state.materialId) &&
              item.hsvs,
          ).hsvs
        }
        if (hsvValue) {
          const temp = _.cloneDeep(state.hsvModel)
          hsvValue.forEach((item, index) => {
            if (item) {
              temp[index] = {
                hub: item[0] as number,
                saturation: item[1] as number,
                value: item[2] as number,
                disabled: false,
              }
            } else {
              temp[index] = {
                hub: 0,
                saturation: 0,
                value: 0,
                disabled: true,
              }
            }
          })
          commit('setHsvValue', hsvValue)
          commit('setHsvModel', temp)
        }

        let shaderParams = modifyData.material?.shaderParams
        if (
          state.modelModifyConfig &&
          state.modelModifyConfig.materials &&
          state.modelModifyConfig.materials &&
          state.modelModifyConfig.materials.some(
            (item) =>
              JSON.stringify(item.id) === JSON.stringify(state.materialId) &&
              item.shaderParams,
          )
        ) {
          shaderParams = state.modelModifyConfig.materials.find((item) => {
            return (
              JSON.stringify(item.id) === JSON.stringify(state.materialId) &&
              item.shaderParams
            )
          }).shaderParams
        }
        if (shaderParams) {
          state.shaderModel = shaderParams
        }
        if (hsvValue || shaderParams) {
          if (!state.resetModel[JSON.stringify(state.materialId)]) {
            const resetTemp = {}
            resetTemp[JSON.stringify(state.materialId)] = {
              hsv: hsvValue,
              shaderParams,
            }
            commit('setResetModel', resetTemp)
          }
        }
      }

      if (updateAnimation && state.animationId) {
        let model = modifyData.animation
        let targetModelAnimationData = state.modelAnimationList.find(
          (item) => item.file_path === state.animationPath,
        )
        // 如果在 modelModifyConfig 中含有数据，则最高优
        if (
          state.modelModifyConfig &&
          state.modelModifyConfig.animations &&
          state.modelModifyConfig.animations &&
          state.modelModifyConfig.animations.some(
            (item) =>
              JSON.stringify(item.id) === JSON.stringify(state.animationId),
          )
        ) {
          targetModelAnimationData = _.cloneDeep(
            state.modelModifyConfig.animations.find(
              (item) =>
                JSON.stringify(item.id) === JSON.stringify(state.animationId),
            ),
          )
        }
        if (targetModelAnimationData) {
          if (
            model?.events?.length === targetModelAnimationData?.events?.length
          ) {
            const getTimeByEvt = (evt) => {
              if (typeof evt === 'number') {
                return evt
              }
              if (evt && evt.time && typeof evt.time === 'number') {
                return evt.time
              }

              return evt
            }
            targetModelAnimationData.events =
              targetModelAnimationData.events.map((evt, idx) => {
                return {
                  time: getTimeByEvt(evt),
                  type: model.events[idx].type || null,
                }
              })
          } else {
            targetModelAnimationData.events = model.events
          }
          model = Object.assign({}, model, targetModelAnimationData)
        }
        if (
          isFeModelId(
            state.modelAnimationList.find(
              (item) => item.file_path === state.animationPath,
            ).id,
          )
        ) {
          const list = updateAnimationListByConfig({
            modelAnimationList: state.modelAnimationList,
            modelAnimation: Object.assign({}, model, {
              id: modifyData.animation.id,
              file_path: state.animationPath,
            }),
          })

          commit(
            'setAnimationModel',
            list.find((item) => item.file_path === state.animationPath),
          )
          commit('setModelAnimationList', list)
        } else {
          commit(
            'setAnimationModel',
            _.pick(model, [
              'id',
              'speed',
              'duration',
              'frameRate',
              'events',
              'rotate',
            ]),
          )
        }
        if (!state.resetModel[JSON.stringify(state.animationId)]) {
          const resetTemp = {}
          const targetModelAnimationData = state.modelAnimationList.find(
            (item) => item.file_path === state.animationPath,
          )
          // 因为 event 字段有特殊处理，并且素材不返回 rotate 属性，因此 rotate 属性通过 targetModelAnimationData 获取
          resetTemp[JSON.stringify(state.animationId)] = Object.assign(
            {},
            state.animationModel,
            {
              rotate: targetModelAnimationData?.rotate || 0,
            },
          )
          commit('setResetModel', resetTemp)
        }
      }
    },

    /**
     * 重置模型编辑器状态
     * @param commit
     * @param status 'open' | 'closed' open 代表打开弹窗的操作，closed 代表关闭弹窗的操作
     */
    resetModelEditorState({ state, commit }, status: 'open' | 'closed') {
      if (status === 'open') {
        commit('resetBasicModel')
        commit('resetHsvModel')
        commit('resetShaderModel')
        commit('resetAnimationModel')
      } else if (status === 'closed') {
        commit('setReplaceData', [])
        commit('setModelMaterialList', [])
        commit('setModelAnimationList', [])
        commit('resetResetModel')
        commit('setReplaceMap', {})
        state.modelPath = ''
        state.materialPath = ''
        state.animationPath = ''
        state.modelId = ''
        state.materialId = ''
        commit('setReplaceModel', false)
        commit('setModelLoading', false)
      }
    },

    resetShaderParamByKey(
      { rootState, state, commit },
      key: keyof ShaderParams,
    ) {
      if (!state.materialId) {
        console.warn('缺少当前编辑的材质 id，无法重置 shader 参数')
        return
      }
      if (
        state.materialId &&
        state.resetModel[JSON.stringify(state.materialId)] &&
        state.resetModel[JSON.stringify(state.materialId)].shaderParams
      ) {
        const resetValue =
          state.resetModel[JSON.stringify(state.materialId)].shaderParams
        commit('setShaderModel', _.pick(resetValue, key))
      } else {
        const resetValue: ShaderParams = {
          mainColor: [0, 0, 0, 1],
          occlusion: 0,
          roughness: 0,
          metallic: 0,
          specularIntensity: 0,
          emissive: [0, 0, 0, 1],
        }
        commit('setShaderModel', _.pick(resetValue, key))
      }
    },

    resetModelModifyConfigByCategoryId({ rootState, state }, { categoryId }) {
      const originalConfig = _.get(
        rootState,
        'creative.template.originalJson.modelModifyConfig',
      )
      const targetCategory = useIdToFindCategory({
        category: rootState.creative.template.templateConfig.category,
        categoryId,
      })
      const modelId =
        targetCategory.properties && targetCategory.properties.modelId
      // 如果当前的 modelModifyConfig 含有数据，则重置
      if (
        modelId &&
        state.modelModifyConfig &&
        state.modelModifyConfig.transforms &&
        state.modelModifyConfig.transforms.find(
          (item) => JSON.stringify(item.id) === JSON.stringify(modelId),
        )
      ) {
        const transformIndex = state.modelModifyConfig.transforms.findIndex(
          (item) => JSON.stringify(item.id) === JSON.stringify(modelId),
        )
        if (
          originalConfig &&
          originalConfig.transforms &&
          originalConfig.transforms.length &&
          originalConfig.transforms.find(
            (item: { id: ModelResId; value: number[] }) =>
              JSON.stringify(item.id) === JSON.stringify(modelId),
          )
        ) {
          const originalValue = originalConfig.transforms.find(
            (item: { id: ModelResId; value: number[] }) =>
              JSON.stringify(item.id) === JSON.stringify(modelId),
          )
          state.modelModifyConfig.transforms[transformIndex].value =
            originalValue.value
        } else {
          state.modelModifyConfig.transforms.splice(transformIndex, 1)
        }
      }
      const materialIds = getMaterialList(targetCategory).map((item) => item.id)
      if (materialIds && materialIds.length) {
        materialIds.forEach((materialId) => {
          if (materialId && state.modelModifyConfig.materials) {
            const index = state.modelModifyConfig.materials.findIndex(
              (item) => JSON.stringify(item.id) === JSON.stringify(materialId),
            )
            if (index !== -1) {
              if (
                originalConfig &&
                originalConfig.materials &&
                originalConfig.materials.length &&
                originalConfig.materials.find(
                  (item: { id: ModelResId; value: number[] }) =>
                    JSON.stringify(item.id) === JSON.stringify(materialId),
                )
              ) {
                const originalValue = originalConfig.materials.find(
                  (item: { id: ModelResId; value: number[] }) =>
                    JSON.stringify(item.id) === JSON.stringify(materialId),
                )
                state.modelModifyConfig.materials[index] = originalValue
              } else {
                state.modelModifyConfig.materials.splice(index, 1)
              }
            }
          }
        })
      }
      if (
        getAnimationList(targetCategory) &&
        getAnimationList(targetCategory).length
      ) {
        getAnimationList(targetCategory).forEach((animationData) => {
          const animationId = animationData.id
          if (animationId && state.modelModifyConfig.animations) {
            const index = state.modelModifyConfig.animations.findIndex(
              (item) => JSON.stringify(item.id) === JSON.stringify(animationId),
            )
            if (index !== -1) {
              if (
                originalConfig &&
                originalConfig.animations &&
                originalConfig.animations.length &&
                originalConfig.animations.find(
                  (item: AnimationData) =>
                    JSON.stringify(item.id) === JSON.stringify(animationId),
                )
              ) {
                const originalValue = originalConfig.animations.find(
                  (item: AnimationData) =>
                    JSON.stringify(item.id) === JSON.stringify(animationId),
                )
                state.modelModifyConfig.animations[index] = originalValue
              } else {
                // 如果动作中有一个动作绑定了多个模型，则不需要删除
                if (animationData.model_category_ids.length === 1) {
                  state.modelModifyConfig.animations.splice(index, 1)
                }
              }
            }
          }
        })
      }
    },

    /**
     * 同步分类属性到 store 中，后续的编辑都是调整 store 数据
     * @param state
     * @param commit
     * @param category
     */
    syncCategoryPropertiesToStore({ state, commit }, category: CookedCategory) {
      if (category.properties) {
        if (category.properties.model_material_list) {
          console.log('同步材质数据')
          commit(
            'setModelMaterialList',
            _.cloneDeep(category.properties.model_material_list),
          )
        } else {
          commit('setModelMaterialList', [])
        }
        if (category.properties.model_animation_list) {
          console.log('同步动画数据')
          commit(
            'setModelAnimationList',
            _.cloneDeep(category.properties.model_animation_list).map(
              (item) => {
                // 确保历史数据也有 rotate
                if (typeof item.rotate === 'undefined') {
                  item.rotate = 0
                }
                return item
              },
            ),
          )
        } else {
          commit('setModelAnimationList', [])
        }
      }
    },
  },
}

export default ModelEditor
